"use client"

import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { getPasswordSchema, getPasswordMessages } from "@/lib/schemas/auth"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getPasswordValidation(
  password: string,
  t: (key: string) => string,
) {
  if (!password) {
    return [false, false, false, false, false]
  }

  const messages = getPasswordMessages(t)
  const schema = getPasswordSchema(t)

  const result = schema.safeParse(password)

  if (result.success) {
    return [true, true, true, true, true]
  }

  const errorMessages = result.error.issues.map((issue) => issue.message)

  return messages.map((message) => !errorMessages.includes(message))
}

interface FieldError {
  [key: string]: string | undefined
}

/**
 * Formats field errors into a user-friendly message
 * @param fieldErrors - Object containing field errors
 * @param fieldLabels - Object mapping field names to display labels
 * @returns Formatted error message or null if no errors
 */
export function formatFieldErrors(
  fieldErrors: FieldError,
  fieldLabels: Record<string, string>,
): string | null {
  const fieldsWithErrors = Object.entries(fieldErrors)
    .filter(([_, error]) => error)
    .map(([fieldName, _]) => {
      const label = fieldLabels[fieldName]
      if (!label) {
        console.warn(`Field "${fieldName}" has an error but no label provided`)
        return `Unknown field (${fieldName})`
      }
      return label
    })

  if (fieldsWithErrors.length === 0) {
    return null
  }

  if (fieldsWithErrors.length === 1) {
    return fieldsWithErrors[0]
  }

  const lastField = fieldsWithErrors.slice(-1)[0]
  const otherFields = fieldsWithErrors.slice(0, -1).join(", ")

  return `${otherFields} and ${lastField}`
}
